/**
 * Quick Add — rebuilt for instant open + reliable drawer UX.
 *
 * Architecture:
 *  - Shared Document cache by canonical /products/{handle}?variant=
 *  - Open dialog on gesture immediately (spinner via :empty)
 *  - Critical fetches are never aborted by nav warmers
 *  - Mount PDP-shaped DOM that quick-add-modal.liquid CSS styles
 *  - Desktop / Mobile gated by Theme.settings.quick_add | mobile_quick_add
 */
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { mediaQueryLarge, onAnimationEnd, isClickedOutside } from '@theme/utilities';
import { getVariantPurchaseState } from '@theme/variant-purchase-state';

/** @type {Map<string, Document>} */
const htmlCache = new Map();
/** @type {Map<string, Promise<Document | null>>} */
const inflight = new Map();
/** @type {Set<AbortController>} */
const abortControllers = new Set();

/** @type {number} */
let openGestureGuardUntil = 0;

/**
 * Abort non-critical prefetch only — never kill an open-path fetch.
 * @returns {void}
 */
export function abortQuickAddFetches() {
  abortControllers.forEach((controller) => {
    if (controller.__pzQuickAddCritical) return;
    try {
      controller.abort();
    } catch {
      /* ignore */
    }
  });
}

if (typeof window !== 'undefined') {
  window.__pzAbortQuickAddFetches = abortQuickAddFetches;
}

/** @returns {void} */
function abortCompetingNavWarm() {
  try {
    window.__pzAbortNavWarm?.();
  } catch {
    /* ignore */
  }
}

/**
 * @param {string} productPageUrl
 * @returns {string}
 */
function normalizeProductUrl(productPageUrl) {
  if (!productPageUrl) return '';
  try {
    const url = new URL(productPageUrl, window.location.origin);
    const match = url.pathname.match(/\/products\/([^/?#]+)/);
    if (!match) return '';
    const root = window.Shopify?.routes?.root || '/';
    const normalized = new URL(`${root}products/${match[1]}`, window.location.origin);
    const variant = url.searchParams.get('variant');
    if (variant) normalized.searchParams.set('variant', variant);
    return normalized.href;
  } catch {
    return productPageUrl;
  }
}

/**
 * Product template section key (templates/product.json → "main").
 * @returns {string}
 */
function getProductSectionId() {
  const dialog = document.getElementById('quick-add-dialog');
  return dialog?.getAttribute('data-product-section-id')?.trim() || 'main';
}

/**
 * @param {string} htmlText
 * @returns {Document}
 */
function parseHtml(htmlText) {
  return new DOMParser().parseFromString(htmlText, 'text/html');
}

/**
 * Section Rendering `?sections=` returns JSON; `?section_id=` returns HTML.
 * @param {string} responseText
 * @returns {Document | null}
 */
function parseFetchPayload(responseText) {
  if (!responseText) return null;
  const trimmed = responseText.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('{')) {
    try {
      const json = JSON.parse(trimmed);
      /** @type {string[]} */
      const htmlChunks = Object.values(json).filter((value) => typeof value === 'string');
      const preferred =
        htmlChunks.find((html) => html.includes('data-product-grid-content')) ||
        htmlChunks.find((html) => html.includes('product-form-component')) ||
        htmlChunks.find((html) => html.includes('product-details')) ||
        htmlChunks[0];
      return preferred ? parseHtml(preferred) : null;
    } catch {
      /* fall through — treat as HTML */
    }
  }

  return parseHtml(trimmed);
}

/**
 * @param {Document | null} doc
 * @returns {boolean}
 */
function hasProductContent(doc) {
  if (!doc) return false;
  return Boolean(
    doc.querySelector('[data-product-grid-content]') ||
      doc.querySelector('.product-details') ||
      doc.querySelector('.product-information') ||
      doc.querySelector('product-form-component[data-product-id]') ||
      doc.querySelector('.product-details__info--mobile-purchase')
  );
}

/**
 * @param {string} text
 * @returns {string}
 */
function decodeHtmlEntities(text) {
  if (typeof text !== 'string' || !text.includes('&')) return text || '';
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

/**
 * Try section HTML → sections JSON → full PDP. Never abort the chain on a
 * single non-OK section response (that was leaving the drawer in permanent error).
 * @param {string} cacheUrl
 * @param {AbortSignal} signal
 * @returns {Promise<Document | null>}
 */
async function loadProductDocument(cacheUrl, signal) {
  const sectionId = getProductSectionId();
  /** @type {string[]} */
  const candidates = [];

  try {
    const sectionUrl = new URL(cacheUrl);
    sectionUrl.searchParams.set('section_id', sectionId);
    candidates.push(sectionUrl.href);

    const sectionsUrl = new URL(cacheUrl);
    sectionsUrl.searchParams.set('sections', sectionId);
    candidates.push(sectionsUrl.href);
  } catch {
    /* ignore — still try full document */
  }

  candidates.push(cacheUrl);

  for (const url of candidates) {
    if (signal.aborted) return null;
    try {
      const response = await fetch(url, {
        signal,
        credentials: 'same-origin',
        cache: 'default',
      });
      if (!response.ok) continue;
      const doc = parseFetchPayload(await response.text());
      if (hasProductContent(doc)) return doc;
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      /* try next candidate */
    }
  }

  return null;
}

/**
 * @param {string} productPageUrl
 * @param {{ critical?: boolean }} [options]
 * @returns {Promise<Document | null>}
 */
async function fetchProductDocument(productPageUrl, options = {}) {
  const cacheUrl = normalizeProductUrl(productPageUrl);
  if (!cacheUrl) return null;

  const cached = htmlCache.get(cacheUrl);
  if (cached) return cached;

  const existing = inflight.get(cacheUrl);
  if (existing) {
    const doc = await existing;
    if (doc || !options.critical) return doc;
  }

  const controller = new AbortController();
  if (options.critical) controller.__pzQuickAddCritical = true;
  abortControllers.add(controller);

  const promise = (async () => {
    try {
      const doc = await loadProductDocument(cacheUrl, controller.signal);
      if (!doc) {
        console.warn('[quick-add] Missing product content', cacheUrl);
        return null;
      }
      htmlCache.set(cacheUrl, doc);
      return doc;
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[quick-add] Fetch failed', error);
      }
      return null;
    } finally {
      abortControllers.delete(controller);
      if (inflight.get(cacheUrl) === promise) inflight.delete(cacheUrl);
    }
  })();

  inflight.set(cacheUrl, promise);
  return promise;
}

/**
 * Soft-load deferred drawer modules (collection templates).
 * @returns {Promise<void>}
 */
async function ensureDrawerStackReady() {
  const loader =
    typeof window !== 'undefined' && typeof window.__pzLoadQuickAddDrawerStack === 'function'
      ? window.__pzLoadQuickAddDrawerStack
      : null;

  if (loader) {
    try {
      await Promise.race([loader(), new Promise((r) => setTimeout(r, 1000))]);
    } catch {
      /* modules may already exist */
    }
  }

  await Promise.all(
    ['product-form-component', 'variant-picker', 'product-price'].map((name) => {
      if (customElements.get(name)) return Promise.resolve();
      return Promise.race([
        customElements.whenDefined(name),
        new Promise((r) => setTimeout(r, 500)),
      ]);
    })
  );
}

export class QuickAddComponent extends Component {
  /** @type {number} */
  #openGuardUntil = 0;
  /** @type {boolean} */
  #opening = false;
  /** @type {number} */
  #mountGen = 0;

  /** @returns {Document | null} */
  get cachedProductHtml() {
    const url = normalizeProductUrl(this.productPageUrl || '');
    return url ? htmlCache.get(url) || null : null;
  }

  /** @returns {string | null} */
  get productPageUrl() {
    const productCard = this.closest(
      'product-card, pz-collection-card, [data-product-url], [data-product-handle]'
    );
    if (!(productCard instanceof HTMLElement)) return null;

    const variantInput = this.querySelector('input[name="id"]');
    const variantId =
      variantInput instanceof HTMLInputElement && variantInput.value ? variantInput.value : '';

    const appendVariant = (href) => {
      try {
        const url = new URL(href, window.location.origin);
        if (variantId) url.searchParams.set('variant', variantId);
        return url.href;
      } catch {
        return href;
      }
    };

    const mainLink =
      productCard.refs?.productCardLink ||
      productCard.querySelector('a.product-card__link') ||
      productCard.querySelector('a[data-card-link]') ||
      productCard.querySelector('a[href*="/products/"]');

    if (mainLink instanceof HTMLAnchorElement && mainLink.href) {
      return appendVariant(mainLink.href);
    }

    const rawUrl = productCard.dataset.productUrl || productCard.getAttribute('data-product-url');
    if (rawUrl) return appendVariant(rawUrl);

    const handle = productCard.dataset.productHandle || productCard.getAttribute('data-product-handle');
    if (!handle) return null;

    const root = window.Shopify?.routes?.root || '/';
    const variantQuery = variantId ? `?variant=${encodeURIComponent(variantId)}` : '';
    return `${root}products/${handle}${variantQuery}`;
  }

  clearCache() {
    if (this.#opening) return;
    const url = normalizeProductUrl(this.productPageUrl || '');
    if (url) {
      htmlCache.delete(url);
      inflight.delete(url);
    }
  }

  /**
   * @param {string} [productPageUrl]
   * @param {{ critical?: boolean }} [options]
   */
  async fetchProductPage(productPageUrl, options = {}) {
    const url = productPageUrl || this.productPageUrl;
    if (!url) return;
    await fetchProductDocument(url, options);
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerdown', this.#onChoosePointerDown, { capture: true });
    this.addEventListener('click', this.#onChooseClick, { capture: true });
    // No hover/focus prefetch: multi-color PDPs (e.g. Grant) return large section HTML.
    // Prefetching on pointerenter across a PLP grid caused freezes; open-path still
    // fetches critically in #onChoosePointerDown / open handlers.
    this.#syncMobileStickyState();
    mediaQueryLarge.addEventListener('change', this.#onBreakpointChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerdown', this.#onChoosePointerDown, { capture: true });
    this.removeEventListener('click', this.#onChooseClick, { capture: true });
    mediaQueryLarge.removeEventListener('change', this.#onBreakpointChange);
  }

  #onBreakpointChange = () => {
    this.#syncMobileStickyState();
    this.#closeModal();
  };

  #syncMobileStickyState = () => {
    this.classList.toggle('quick-add--mobile-sticky', !mediaQueryLarge.matches);
  };

  /** @returns {boolean} */
  #isEnabledForViewport() {
    if (this.hasAttribute('stay-visible') || this.classList.contains('quick-add--direct')) {
      return true;
    }
    const settings = typeof Theme !== 'undefined' ? Theme.settings : null;
    if (!settings) return true;
    // Match Liquid/CSS: only show when the viewport's setting is explicitly on.
    return Boolean(mediaQueryLarge.matches ? settings.quick_add === true : settings.mobile_quick_add === true);
  }

  /**
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #getChooseButton(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;
    const button = target.closest('.quick-add__button--choose');
    if (!button || !this.contains(button)) return null;
    if (button instanceof HTMLButtonElement && button.disabled) {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
    }
    return /** @type {HTMLElement} */ (button);
  }

  /** @param {Event} event */
  #onChoosePointerDown = (event) => {
    if (!(event instanceof PointerEvent)) return;
    if (!this.#getChooseButton(event)) return;

    // Desktop: warm section HTML on press (intent) without opening — cuts open latency.
    if (event.pointerType === 'mouse') {
      const url = normalizeProductUrl(this.productPageUrl || '');
      if (url && !htmlCache.has(url) && !inflight.has(url)) {
        void fetchProductDocument(url, { critical: false });
      }
      return;
    }

    event.stopPropagation();
    void this.handleClick(event);
  };

  /** @param {Event} event */
  #onChooseClick = (event) => {
    if (!this.#getChooseButton(event)) return;
    event.preventDefault();
    event.stopPropagation();
    void this.handleClick(event);
  };

  /**
   * Open drawer — instant shell, then fill from shared cache / network.
   * @param {Event} event
   */
  handleClick = async (event) => {
    event.preventDefault?.();
    event.stopPropagation?.();

    if (!this.#isEnabledForViewport()) return;

    const now = Date.now();
    if (this.#opening || now < this.#openGuardUntil || now < openGestureGuardUntil) return;
    this.#opening = true;
    this.#openGuardUntil = now + 500;
    openGestureGuardUntil = now + 500;

    const rawUrl = this.productPageUrl;
    const productUrl = normalizeProductUrl(rawUrl || '');
    if (!productUrl) {
      this.#opening = false;
      console.warn('[quick-add] No product URL');
      return;
    }

    abortCompetingNavWarm();
    const stackReady = ensureDrawerStackReady();

    // Warm modules + open within the user gesture (required for mobile showModal).
    void this.#ensureZoomModules();
    this.#openModal();

    const modalContent = document.getElementById('quick-add-modal-content');
    modalContent?.replaceChildren();

    const button =
      (event.target instanceof Element && event.target.closest('.quick-add__button')) ||
      this.querySelector('.quick-add__button--choose, .quick-add__button');
    if (button instanceof HTMLElement) button.setAttribute('aria-busy', 'true');

    try {
      // Kick fetch immediately (critical). Do not wait on module stack before mount —
      // that was adding up to ~800ms of dead time after HTML was already ready (INP).
      let doc = htmlCache.get(productUrl) || null;
      const fetchPromise = doc ? Promise.resolve(doc) : fetchProductDocument(productUrl, { critical: true });
      doc = (await fetchPromise) || htmlCache.get(productUrl) || null;

      // One retry if a race left us empty.
      if (!doc) {
        doc = await fetchProductDocument(productUrl, { critical: true });
      }

      if (!doc) {
        this.#showLoadError(productUrl);
        return;
      }

      const mounted = this.#mountDrawer(doc);
      if (!mounted) {
        this.#showLoadError(productUrl);
        return;
      }

      // Upgrade CEs when stack is ready (short race so UI isn't blocked).
      void Promise.race([stackReady, new Promise((r) => setTimeout(r, 400))]).then(() => {
        const host = document.getElementById('quick-add-modal-content');
        if (host && typeof customElements.upgrade === 'function') {
          customElements.upgrade(host);
        }
      });
    } catch (error) {
      console.warn('[quick-add] Open failed', error);
      this.#showLoadError(productUrl);
    } finally {
      this.#opening = false;
      if (button instanceof HTMLElement) button.removeAttribute('aria-busy');
    }
  };

  #openModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!dialogComponent) return;

    // Stay visible on mobile while dialog is open (bag overlay host).
    if (!mediaQueryLarge.matches) {
      this.setAttribute('stay-visible', '');
      const onClose = () => this.removeAttribute('stay-visible');
      dialogComponent.addEventListener(DialogCloseEvent.eventName, onClose, { once: true });
    }

    if (typeof dialogComponent.showDialog === 'function') {
      dialogComponent.showDialog();
      return;
    }

    const dialog = dialogComponent.querySelector('.quick-add-modal');
    if (dialog instanceof HTMLDialogElement && !dialog.open) dialog.showModal();
  };

  #closeModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!dialogComponent) return;
    if (typeof dialogComponent.closeDialog === 'function') {
      dialogComponent.closeDialog();
      return;
    }
    const dialog = dialogComponent.querySelector('.quick-add-modal');
    if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
  };

  /**
   * @param {string} productUrl
   */
  #showLoadError(productUrl) {
    this.#mountGen += 1;
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!(modalContent instanceof HTMLElement)) {
      this.#closeModal();
      return;
    }

    const message = decodeHtmlEntities(
      (typeof Theme !== 'undefined' && Theme.translations?.quick_add_load_error) ||
        "Couldn't load this product. Try again."
    );
    const retryLabel = decodeHtmlEntities(
      (typeof Theme !== 'undefined' && Theme.translations?.collection_try_again) || 'Try again'
    );
    const closeLabel = decodeHtmlEntities(
      (typeof Theme !== 'undefined' && Theme.translations?.quick_add_close) || 'Close'
    );

    const panel = document.createElement('div');
    panel.className = 'quick-add-modal__error';
    panel.setAttribute('role', 'alert');

    const text = document.createElement('p');
    text.className = 'quick-add-modal__error-message';
    text.textContent = message;

    const actions = document.createElement('div');
    actions.className = 'quick-add-modal__error-actions';

    const retryBtn = document.createElement('button');
    retryBtn.type = 'button';
    retryBtn.className = 'button quick-add-modal__error-retry';
    retryBtn.textContent = retryLabel;
    retryBtn.addEventListener('click', () => {
      htmlCache.delete(productUrl);
      inflight.delete(productUrl);
      this.#opening = false;
      this.#openGuardUntil = 0;
      openGestureGuardUntil = 0;
      modalContent.replaceChildren();
      void this.handleClick(
        /** @type {Event} */ ({
          preventDefault() {},
          stopPropagation() {},
          target: this.querySelector('.quick-add__button--choose') || this,
        })
      );
    });

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'button button-secondary quick-add-modal__error-close';
    closeBtn.textContent = closeLabel;
    closeBtn.addEventListener('click', () => this.#closeModal());

    actions.append(retryBtn, closeBtn);
    panel.append(text, actions);
    modalContent.replaceChildren(panel);
  }

  /**
   * @param {Document} doc
   * @returns {boolean}
   */
  #mountDrawer(doc) {
    const productGrid =
      doc.querySelector('[data-product-grid-content]') ||
      doc.querySelector('.product-information') ||
      doc.querySelector('.product-details')?.parentElement ||
      doc.body;
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) return false;

    // Progressive mount (INP): paint title/ATC first, fill media on the next frame.
    // Avoid cloning the entire PDP grid — only clone the pieces we need.
    let body = null;
    try {
      body = this.#buildDrawerShell(productGrid);
    } catch (error) {
      console.warn('[quick-add] Build failed', error);
      return false;
    }
    if (!body) return false;

    const mountGen = ++this.#mountGen;
    modalContent.replaceChildren(body);

    // Upgrade purchase CEs immediately so color→size availability matches PDP (INP-safe:
    // media still mounts on the next frame).
    if (typeof customElements.upgrade === 'function') {
      customElements.upgrade(body);
    }

    try {
      this.#initPurchaseUi(modalContent);
    } catch (error) {
      console.warn('[quick-add] Purchase UI init failed', error);
    }

    if (modalContent.dataset.qaGalleryBound !== 'true') {
      modalContent.dataset.qaGalleryBound = 'true';
      modalContent.addEventListener('media-gallery:replaced', () => {
        requestAnimationFrame(() => {
          void this.#finalizeGallery(modalContent);
        });
      });
    }

    // Frame 1: media (+ deferred map). Frame 2: finalize gallery + zoom dialog.
    requestAnimationFrame(() => {
      if (this.#mountGen !== mountGen || !modalContent.isConnected) return;
      try {
        this.#appendDrawerMedia(body, productGrid);
      } catch (error) {
        console.warn('[quick-add] Media mount failed', error);
      }
      if (typeof customElements.upgrade === 'function') {
        customElements.upgrade(body);
      }
      requestAnimationFrame(() => {
        if (this.#mountGen !== mountGen || !modalContent.isConnected) return;
        void this.#finalizeGallery(modalContent);
        try {
          this.#appendDrawerZoom(body, productGrid);
        } catch (error) {
          console.warn('[quick-add] Zoom mount failed', error);
        }
      });
    });

    return true;
  }

  /**
   * Title + purchase only — reserves a media slot so layout does not jump.
   * @param {Element} productGrid
   * @returns {HTMLElement | null}
   */
  #buildDrawerShell(productGrid) {
    const mobileHero = productGrid.querySelector('.product-details__info--mobile-hero');
    const mobilePurchase = productGrid.querySelector('.product-details__info--mobile-purchase');
    const sharedBuy = productGrid.querySelector('.product-details__info--shared-buy');
    const productForm =
      sharedBuy?.querySelector('product-form-component') ||
      productGrid.querySelector('product-form-component');

    const body = document.createElement('div');
    body.className = 'quick-add-modal__drawer-body quick-add-modal__drawer-body--pdp';

    const galleryRoot =
      productGrid.querySelector('[data-media-gallery-full-width]') ||
      productGrid.querySelector('.product-information__media')?.closest('[data-media-gallery-full-width]');

    if (galleryRoot instanceof HTMLElement) {
      ['data-pdp-media-sizes', 'data-zoom-media-sizes', 'data-thumbnail-media-sizes'].forEach((attr) => {
        const value = galleryRoot.getAttribute(attr);
        if (value) body.setAttribute(attr, value);
      });
    }

    // Empty media host keeps flex layout stable until the gallery clone lands.
    const mediaSlot = document.createElement('div');
    mediaSlot.className =
      'quick-add-modal__media product-information__media product-information__media--full-width';
    mediaSlot.setAttribute('data-qa-media-slot', 'true');
    if (galleryRoot instanceof HTMLElement) {
      const infinite = galleryRoot.getAttribute('data-gallery-enable-infinite');
      if (infinite != null) mediaSlot.setAttribute('data-gallery-enable-infinite', infinite);
    }
    body.appendChild(mediaSlot);

    const details = document.createElement('div');
    details.className = 'product-details quick-add-modal__details-wrap';

    if (mobileHero && mobilePurchase) {
      const hero = /** @type {HTMLElement} */ (mobileHero.cloneNode(true));
      const purchase = /** @type {HTMLElement} */ (mobilePurchase.cloneNode(true));
      hero.classList.remove('product-details__info--mobile-only');
      purchase.classList.remove('product-details__info--mobile-only');
      this.#pruneQuickAddNoise(hero);
      this.#pruneQuickAddNoise(purchase);
      this.#mergeBuyButtons(purchase, sharedBuy, productForm);
      this.#normalizeTitle(hero);
      this.#tagPickers(purchase);
      purchase.querySelectorAll('product-inventory').forEach((el) => el.remove());
      details.append(hero, purchase);
    } else {
      const fallback =
        productGrid.querySelector('.product-details') ||
        productGrid.querySelector('[data-product-grid-content]') ||
        productGrid;
      const clone = /** @type {HTMLElement} */ (fallback.cloneNode(true));
      clone.classList.add('product-details', 'quick-add-modal__details-fallback');
      this.#pruneQuickAddNoise(clone);
      clone.querySelectorAll('product-inventory').forEach((el) => el.remove());
      // Fallback may include a full media tree — strip it; media mounts via the slot.
      clone
        .querySelectorAll(
          '.product-information__media, [data-media-gallery-full-width], media-gallery, zoom-dialog'
        )
        .forEach((el) => el.remove());
      this.#tagPickers(clone);
      details.appendChild(clone);
    }

    body.appendChild(details);
    return body;
  }

  /**
   * @param {HTMLElement} body
   * @param {Element} productGrid
   */
  #appendDrawerMedia(body, productGrid) {
    const existing = body.querySelector('.quick-add-modal__media');
    const galleryRoot =
      productGrid.querySelector('[data-media-gallery-full-width]') ||
      productGrid.querySelector('.product-information__media')?.closest('[data-media-gallery-full-width]');
    const productMedia =
      galleryRoot?.querySelector('.product-information__media') ||
      productGrid.querySelector('.product-information__media');

    if (!(productMedia instanceof HTMLElement)) {
      existing?.remove();
      return;
    }

    const media = /** @type {HTMLElement} */ (productMedia.cloneNode(true));
    media.classList.add(
      'quick-add-modal__media',
      'product-information__media',
      'product-information__media--full-width'
    );
    // Trim PDP-only chrome before insert (INP + avoids flash of wrong chrome).
    media.querySelectorAll('[data-gallery-clone], [data-gallery-scroll-spacer]').forEach((el) => el.remove());
    media.querySelectorAll('[data-gallery-zoom-trigger], .media-gallery__zoom-button').forEach((el) => el.remove());

    if (galleryRoot instanceof HTMLElement) {
      const infinite = galleryRoot.getAttribute('data-gallery-enable-infinite');
      if (infinite != null) media.setAttribute('data-gallery-enable-infinite', infinite);

      const mapScript = galleryRoot.querySelector('script[data-pdp-deferred-media-map]');
      if (mapScript && !body.querySelector('script[data-pdp-deferred-media-map]')) {
        body.prepend(mapScript.cloneNode(true));
      }
    }

    if (existing) {
      existing.replaceWith(media);
    } else {
      const details = body.querySelector('.quick-add-modal__details-wrap');
      if (details) body.insertBefore(media, details);
      else body.prepend(media);
    }
  }

  /**
   * Zoom dialog is heavy and only needed on tap — mount after first gallery paint.
   * @param {HTMLElement} body
   * @param {Element} productGrid
   */
  #appendDrawerZoom(body, productGrid) {
    if (body.querySelector('zoom-dialog')) return;

    const galleryRoot =
      productGrid.querySelector('[data-media-gallery-full-width]') ||
      productGrid.querySelector('.product-information__media')?.closest('[data-media-gallery-full-width]');
    const zoom =
      galleryRoot?.querySelector(':scope > zoom-dialog') || productGrid.querySelector('zoom-dialog');
    if (!(zoom instanceof HTMLElement)) return;

    const clone = /** @type {HTMLElement} */ (zoom.cloneNode(true));
    clone.id = `zoom-dialog-quick-add-${Date.now()}`;
    const details = body.querySelector('.quick-add-modal__details-wrap');
    const media = body.querySelector('.quick-add-modal__media');
    if (details) body.insertBefore(clone, details);
    else if (media) media.after(clone);
    else body.appendChild(clone);

    void this.#ensureZoomModules();
  }

  /**
   * Drop large non-interactive PDP chrome from cloned purchase/title trees.
   * Keep variant-picker JSON — sizes/ATC availability depend on it (same as PDP).
   * @param {HTMLElement} root
   */
  #pruneQuickAddNoise(root) {
    root.querySelectorAll('script').forEach((el) => {
      if (!(el instanceof HTMLScriptElement)) return;
      if (el.hasAttribute('data-pdp-deferred-media-map')) return;
      if (el.hasAttribute('data-product-variants-config')) return;
      if (el.getAttribute('type') === 'application/json') return;
      el.remove();
    });
    root
      .querySelectorAll('template, noscript, iframe, product-recommendations, .shopify-app-block')
      .forEach((el) => el.remove());
  }

  /**
   * @param {HTMLElement} purchase
   * @param {Element | null} sharedBuy
   * @param {Element | null} productForm
   */
  #mergeBuyButtons(purchase, sharedBuy, productForm) {
    if (purchase.querySelector('product-form-component, .buy-buttons-block, .product-form-buttons')) {
      return;
    }
    const buySource =
      sharedBuy?.querySelector('.product-details__block--buy-buttons') ||
      sharedBuy?.querySelector('product-form-component')?.closest('.product-details__block--buy-buttons') ||
      productForm?.closest('.product-details__block--buy-buttons') ||
      productForm;
    if (!buySource) return;

    const block = document.createElement('div');
    block.className = 'product-details__block product-details__block--buy-buttons';
    if (buySource.matches?.('.product-details__block--buy-buttons')) {
      block.append(...Array.from(buySource.cloneNode(true).childNodes));
    } else {
      block.appendChild(buySource.cloneNode(true));
    }
    this.#pruneQuickAddNoise(block);
    purchase.appendChild(block);
  }

  /** @param {HTMLElement} hero */
  #normalizeTitle(hero) {
    const titleBlock = hero.querySelector('.product-details__block--title');
    if (!titleBlock) return;
    const heading =
      titleBlock.querySelector('.product-title-heading') ||
      titleBlock.querySelector('a.product-title-heading') ||
      titleBlock.querySelector('h3');
    const subheading =
      titleBlock.querySelector('.product-title-subheading') || titleBlock.querySelector('h4');
    if (!(heading instanceof HTMLElement)) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'product-title-wrapper';
    const row = document.createElement('div');
    row.className = 'product-title-row';
    const text = document.createElement('div');
    text.className = 'product-title-text';
    text.appendChild(heading.cloneNode(true));
    if (subheading instanceof HTMLElement) text.appendChild(subheading.cloneNode(true));
    row.appendChild(text);
    wrapper.appendChild(row);
    titleBlock.replaceChildren(wrapper);
  }

  /** @param {HTMLElement} root */
  #tagPickers(root) {
    root.querySelectorAll('variant-picker').forEach((picker) => {
      if (!(picker instanceof HTMLElement)) return;
      picker.setAttribute('data-pdp-variant-picker', 'true');
      picker.setAttribute('data-template-product-match', 'true');
    });
  }

  /** @param {HTMLElement} modalContent */
  #initPurchaseUi(modalContent) {
    const clearAppProxy = () => {
      modalContent.querySelectorAll('.buy-buttons-block').forEach((block) => {
        block.classList.remove(
          'buy-buttons-block--app-cta',
          'buy-buttons-block--app-notify',
          'buy-buttons-block--app-preorder',
          'buy-buttons-block--variant-pending'
        );
      });
      modalContent.querySelectorAll('.app-proxy-cta, [data-app-proxy-cta]').forEach((proxy) => {
        proxy.setAttribute('hidden', '');
        if (proxy instanceof HTMLElement) {
          proxy.dataset.mode = 'none';
          proxy.style.setProperty('display', 'none', 'important');
        }
      });
    };

    const apply = () => {
      clearAppProxy();
      const form = modalContent.querySelector('product-form-component[data-product-id]');
      const variantInput = form?.querySelector('input[name="id"]');
      const variantId = variantInput instanceof HTMLInputElement ? variantInput.value : '';
      if (!variantId) return;

      // Variants live on the PDP picker config — not inside the buy-buttons form.
      let variant = /** @type {object} */ ({ id: variantId });
      try {
        const config =
          modalContent.querySelector('script[data-product-variants-config]') ||
          form?.querySelector('[type="application/json"]');
        if (config?.textContent) {
          const data = JSON.parse(config.textContent);
          const variants = data?.variants || data?.product?.variants || [];
          const match = variants.find((v) => String(v.id) === String(variantId));
          if (match) variant = match;
        }
      } catch {
        /* enrichVariantInventory still resolves from picker scope */
      }

      const state = getVariantPurchaseState(variant, modalContent);
      // Drawer policy: Add for in-stock + continue-selling; Sold out otherwise.
      const canPurchase = state.mode === 'add_to_cart' || state.mode === 'pre_order';
      const isSoldOut = !canPurchase;
      const atc =
        modalContent.querySelector('[ref="addToCartButton"]') ||
        modalContent.querySelector('button[type="submit"][name="add"]');
      if (!(atc instanceof HTMLButtonElement)) return;

      if (!atc.dataset.qaDefaultLabel) {
        const t = (atc.querySelector('.add-to-cart-text__content')?.textContent || '').trim();
        if (t && !/sold out|unavailable|notify|pre-?\s?order/i.test(t)) {
          atc.dataset.qaDefaultLabel = t;
        }
      }

      atc.disabled = isSoldOut;
      atc.setAttribute('aria-disabled', isSoldOut ? 'true' : 'false');
      atc.classList.toggle('button--sold-out', isSoldOut);
      const label = isSoldOut
        ? state.mode === 'unavailable'
          ? Theme.translations?.unavailable || 'Unavailable'
          : Theme.translations?.sold_out || 'Sold out'
        : atc.dataset.qaDefaultLabel || Theme.translations?.add_to_cart || 'Add to cart';
      atc.querySelectorAll('.add-to-cart-text__content').forEach((el) => {
        el.textContent = label;
      });
    };

    apply();
    if (modalContent.dataset.qaPurchaseBound === 'true') return;
    modalContent.dataset.qaPurchaseBound = 'true';
    modalContent.addEventListener(ThemeEvents.variantUpdate, () => {
      requestAnimationFrame(apply);
    });
    // Optimistic drawer updates from variant-picker (local JSON, no section wait).
    modalContent.addEventListener('quick-add:purchase-refresh', () => {
      requestAnimationFrame(apply);
    });
  }

  async #ensureZoomModules() {
    if (customElements.get('zoom-dialog') && customElements.get('drag-zoom-wrapper')) return;
    try {
      await Promise.all([
        import('@theme/zoom-dialog').catch(() => null),
        import('@theme/drag-zoom-wrapper').catch(() => null),
      ]);
    } catch {
      /* optional */
    }
  }

  /**
   * Gallery: match drawer CSS contracts (peek mobile / multi-column desktop).
   * @param {HTMLElement} modalContent
   */
  async #finalizeGallery(modalContent) {
    const media = modalContent.querySelector('.quick-add-modal__media');
    if (!(media instanceof HTMLElement)) return;

    // Drop PDP infinite clones / zoom chrome copied from section HTML.
    media.querySelectorAll('[data-gallery-clone], [data-gallery-scroll-spacer]').forEach((el) => el.remove());
    media.querySelectorAll('[data-gallery-zoom-trigger], .media-gallery__zoom-button').forEach((el) => el.remove());

    const gallery = media.querySelector('media-gallery');
    if (gallery instanceof HTMLElement) {
      if (customElements.get('media-gallery') == null) {
        try {
          await Promise.race([
            customElements.whenDefined('media-gallery'),
            new Promise((r) => setTimeout(r, 400)),
          ]);
        } catch {
          /* ignore */
        }
      }
      if (typeof customElements.upgrade === 'function') customElements.upgrade(gallery);
    }

    const slideshow = media.querySelector('slideshow-component');
    if (!(slideshow instanceof HTMLElement)) {
      void this.#playVideos(media);
      return;
    }

    if (typeof customElements.upgrade === 'function') customElements.upgrade(slideshow);

    slideshow.classList.add('slideshow--multiple-visible', 'slideshow--single-scroll');
    slideshow.removeAttribute('disabled');

    // Land on the active color first (inject deferred shells if needed) — do not
    // unhide every slide before filtering (wrong-color flash + wasted work).
    const checkedSwatch = modalContent.querySelector(
      '.variant-option--swatches input[type="radio"]:checked, input[type="radio"][data-media-color-key]:checked'
    );
    const colorValue =
      checkedSwatch instanceof HTMLInputElement
        ? checkedSwatch.getAttribute('data-media-color-key') || checkedSwatch.value || ''
        : '';

    const drawerBody = media.closest('.quick-add-modal__drawer-body');
    if (colorValue && drawerBody && typeof window.ensureDeferredColorMediaInRoot === 'function') {
      // Imperative inject in case media-gallery listener is not connected yet.
      window.ensureDeferredColorMediaInRoot(drawerBody, colorValue);
    }

    if (colorValue) {
      modalContent.dispatchEvent(
        new CustomEvent('variant:update', {
          bubbles: true,
          detail: {
            resource: null,
            sourceId: '',
            data: {
              colorValue,
              productId: '',
              mediaOnly: true,
            },
          },
        })
      );

      // Fallback filter if media-gallery hasn't connected yet (first open race).
      const normalized =
        typeof window.normalizeColorForMedia === 'function'
          ? window.normalizeColorForMedia(colorValue)
          : String(colorValue).toLowerCase().replace(/[^a-z0-9]/g, '');
      let visibleForColor = 0;
      slideshow.querySelectorAll('slideshow-slide:not([data-gallery-clone])').forEach((slide) => {
        if (!(slide instanceof HTMLElement)) return;
        const role = slide.getAttribute('data-media-role') || '';
        if (role === 'swatch') {
          slide.hidden = true;
          slide.style.display = 'none';
          return;
        }
        const mediaColor = (
          typeof window.normalizeColorForMedia === 'function'
            ? window.normalizeColorForMedia(slide.getAttribute('data-media-color') || '')
            : String(slide.getAttribute('data-media-color') || '')
                .toLowerCase()
                .replace(/[^a-z0-9]/g, '')
        );
        const match = Boolean(normalized && mediaColor && mediaColor === normalized);
        if (match) visibleForColor += 1;
        slide.hidden = !match;
        slide.style.display = match ? '' : 'none';
        slide.setAttribute('aria-hidden', match ? 'false' : 'true');
        if (match) slide.setAttribute('reveal', '');
        else slide.removeAttribute('reveal');
      });
      if (visibleForColor > 0 && typeof window.hydrateDeferredProductMedia === 'function') {
        window.hydrateDeferredProductMedia(media);
        if (drawerBody) window.hydrateDeferredProductMedia(drawerBody);
      }
    } else {
      const slides = slideshow.querySelectorAll('slideshow-slide:not([data-gallery-clone])');
      slides.forEach((slide) => {
        if (!(slide instanceof HTMLElement)) return;
        if ((slide.getAttribute('data-media-role') || '') === 'swatch') {
          slide.setAttribute('hidden', '');
          slide.style.display = 'none';
          return;
        }
        slide.removeAttribute('hidden');
        slide.style.display = '';
        slide.style.opacity = '';
        slide.style.visibility = '';
        slide.setAttribute('aria-hidden', 'false');
        slide.setAttribute('reveal', '');
      });
      if (typeof window.hydrateDeferredProductMedia === 'function') {
        window.hydrateDeferredProductMedia(media);
      }
    }

    const visibleSlides = Array.from(
      slideshow.querySelectorAll('slideshow-slide:not([data-gallery-clone])')
    ).filter((slide) => {
      if (!(slide instanceof HTMLElement)) return false;
      if (slide.hidden || slide.getAttribute('aria-hidden') === 'true') return false;
      if (slide.style.display === 'none') return false;
      return true;
    });

    const isPhone =
      window.matchMedia('(max-width: 749px)').matches ||
      window.matchMedia('(hover: none)').matches ||
      window.matchMedia('(pointer: coarse)').matches;

    const scroller = slideshow.querySelector('slideshow-slides');

    if (isPhone) {
      media.classList.add('quick-add-modal__media--peek', 'product-information__media--single-column-preview');
      media.classList.remove('quick-add-modal__media--multi-column', 'product-information__media--height-led');
      if (scroller instanceof HTMLElement) {
        scroller.style.setProperty('--slides-per-view', '1');
        scroller.style.overflowX = 'auto';
        scroller.style.scrollSnapType = 'x mandatory';
        scroller.style.webkitOverflowScrolling = 'touch';
        scroller.style.touchAction = 'pan-x';
      }
      if (typeof /** @type {any} */ (slideshow).select === 'function') {
        /** @type {any} */ (slideshow).select(0, undefined, { animate: false });
      }
    } else {
      // Desktop: multi-image row inside a 2:3-tall frame (CSS sets height).
      media.classList.add('quick-add-modal__media--multi-column', 'product-information__media--height-led');
      media.classList.remove('quick-add-modal__media--peek', 'product-information__media--single-column-preview');
      if (scroller instanceof HTMLElement) {
        scroller.style.removeProperty('--slides-per-view');
        scroller.scrollLeft = 0;
      }
    }

    const controls = media.querySelector('slideshow-controls');
    if (controls instanceof HTMLElement) {
      controls.hidden = visibleSlides.length <= 1;
      controls.style.display = visibleSlides.length <= 1 ? 'none' : 'flex';
    }
    const arrows = media.querySelector('slideshow-arrows');
    if (arrows instanceof HTMLElement) {
      arrows.hidden = visibleSlides.length <= 1;
      arrows.style.display = visibleSlides.length <= 1 ? 'none' : '';
    }

    // Unique zoom dialog ids so PDP handlers don't collide.
    modalContent.querySelectorAll('zoom-dialog').forEach((zoom, index) => {
      if (!(zoom instanceof HTMLElement)) return;
      zoom.id = `zoom-dialog-quick-add-${Date.now()}-${index}`;
    });

    // Only autoplay videos in currently visible slides (INP).
    void this.#playVideos(media, { visibleOnly: true });
  }

  /**
   * @param {HTMLElement} mediaHost
   * @param {{ visibleOnly?: boolean }} [options]
   */
  async #playVideos(mediaHost, options = {}) {
    if (customElements.get('deferred-media') == null) {
      try {
        await customElements.whenDefined('deferred-media');
      } catch {
        /* ignore */
      }
    }
    if (typeof customElements.upgrade === 'function') {
      mediaHost.querySelectorAll('deferred-media').forEach((el) => customElements.upgrade(el));
    }

    const visibleOnly = Boolean(options.visibleOnly);
    mediaHost.querySelectorAll('deferred-media').forEach((deferred) => {
      if (!(deferred instanceof HTMLElement)) return;
      if (visibleOnly) {
        const slide = deferred.closest('slideshow-slide');
        if (
          slide instanceof HTMLElement &&
          (slide.hidden ||
            slide.getAttribute('aria-hidden') === 'true' ||
            slide.style.display === 'none')
        ) {
          return;
        }
      }
      deferred.setAttribute('autoplay', '');
      let video = deferred.querySelector('video');
      if (!video && !deferred.getAttribute('data-media-loaded')) {
        if (typeof /** @type {any} */ (deferred).loadContent === 'function') {
          /** @type {any} */ (deferred).loadContent(false);
        } else {
          const template = deferred.querySelector('template');
          const content = template?.content?.firstElementChild?.cloneNode(true);
          if (content) {
            deferred.setAttribute('data-media-loaded', 'true');
            deferred.appendChild(content);
          }
        }
        video = deferred.querySelector('video');
      }
      deferred.querySelector('.deferred-media__poster-button')?.classList.add('deferred-media__playing');
      this.#playVideo(video);
    });

    mediaHost.querySelectorAll('video').forEach((video) => {
      if (visibleOnly) {
        const slide = video.closest('slideshow-slide');
        if (
          slide instanceof HTMLElement &&
          (slide.hidden ||
            slide.getAttribute('aria-hidden') === 'true' ||
            slide.style.display === 'none')
        ) {
          return;
        }
      }
      this.#playVideo(video);
    });
  }

  /** @param {HTMLVideoElement | null} video */
  #playVideo(video) {
    if (!(video instanceof HTMLVideoElement)) return;
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.autoplay = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    const play = () => {
      const p = video.play();
      if (p?.catch) p.catch(() => {});
    };
    if (video.readyState >= 2) play();
    else video.addEventListener('loadeddata', play, { once: true });
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

/* Touch fallback: card media-link can steal the bag tap before CE handlers. */
if (!window.__pzQuickAddChooseBound) {
  window.__pzQuickAddChooseBound = true;
  window.addEventListener(
    'pointerup',
    (event) => {
      if (!(event instanceof PointerEvent) || event.pointerType === 'mouse') return;
      if (Date.now() < openGestureGuardUntil) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const button = target.closest('.quick-add__button--choose');
      if (!button) return;
      const component = button.closest('quick-add-component');
      if (!component || typeof /** @type {any} */ (component).handleClick !== 'function') return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      /** @type {any} */ (component).handleClick(event);
    },
    true
  );
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  #ignoreOutsideCloseUntil = 0;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    // Keep drawer variant updates inside the dialog — bubbling to document was
    // filtering the live PDP gallery to the drawer's color (empty until click).
    this.addEventListener(
      ThemeEvents.variantUpdate,
      (event) => {
        this.#updateProductTitleLink(event);
        event.stopPropagation();
      },
      { signal: this.#abortController.signal }
    );
    this.addEventListener(
      'media-gallery:replaced',
      (event) => {
        event.stopPropagation();
      },
      { signal: this.#abortController.signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  showDialog() {
    const dialog = this.querySelector('.quick-add-modal');
    if (!(dialog instanceof HTMLDialogElement)) return;

    if (dialog.open) {
      this.#ignoreOutsideCloseUntil = Date.now() + 700;
      return;
    }

    dialog.showModal();
    this.dispatchEvent(new DialogOpenEvent());
    this.#ignoreOutsideCloseUntil = Date.now() + 700;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    setTimeout(() => {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  closeDialog = async () => {
    const dialog = this.querySelector('.quick-add-modal');
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    this.querySelectorAll(':scope > zoom-dialog').forEach((zoom) => {
      const inner = zoom.querySelector('dialog');
      if (inner?.open) inner.close();
      zoom.remove();
    });

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');
    await onAnimationEnd(dialog, undefined, { subtree: false });
    dialog.close();
    dialog.classList.remove('dialog-closing');
    this.dispatchEvent(new DialogCloseEvent());
  };

  #handleClick = (event) => {
    if (Date.now() < this.#ignoreOutsideCloseUntil) return;
    const dialog = this.querySelector('.quick-add-modal');
    if (!dialog) return;
    if (event.target instanceof Element && dialog.contains(event.target) && event.target !== dialog) {
      return;
    }
    if (isClickedOutside(event, dialog)) this.closeDialog();
  };

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  };

  handleCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (event) => {
    const anchor =
      event.detail?.data?.html?.querySelector('.view-product-title a') ||
      event.detail?.data?.html?.querySelector('.product-title-heading');
    if (!anchor) return;
    const mobileTitle =
      this.querySelector('.product-details__info--mobile-hero .product-title-heading') ||
      this.querySelector('.product-header a');
    if (mobileTitle) mobileTitle.href = anchor.href;
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
